import { useCallback } from "react";
import { FiatCurrency } from "@nestcoinco/hyper-api-gateway-api-client";
import useExchangeRatesQuery from "../react-query/queries/use-exchange-rates";
import useAuth from "./useAuth";
import { formatFiatWithAbbreviation, formatHprBalance } from "../utils/helpers";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FEATURE_FLAGS } from "../config/feature-flags";

export interface HprFiatFormatterResult {
  isFiatDisplay: boolean;
  formatHprToFiatValue: (amount?: number, threshold?: number) => string;
}

interface UseHprFiatFormatterOptions {
  forceFiatDisplay?: boolean;
  currency?: FiatCurrency;
}

export default function useHprFiatFormatter({
  forceFiatDisplay = false,
  currency,
}: UseHprFiatFormatterOptions = {}): HprFiatFormatterResult {
  const { userProfile } = useAuth();
  const userCurrency =
    currency ?? (userProfile?.currency as FiatCurrency | undefined);
  const { getExchangeRate } = useExchangeRatesQuery();
  const isFiatDisplayEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.DISPLAY_USER_CURRENCY
  );

  const exchangeRate = userCurrency ? getExchangeRate(userCurrency) : undefined;
  const isFiatDisplay = !!(
    (forceFiatDisplay || isFiatDisplayEnabled) &&
    userCurrency &&
    exchangeRate
  );

  const formatHprToFiatValue = useCallback(
    (val?: number, threshold?: number): string => {
      if (val === undefined) return "--";
      if (!isFiatDisplay || !exchangeRate) return formatHprBalance(val);
      return formatFiatWithAbbreviation(
        val * exchangeRate,
        userCurrency,
        threshold
      );
    },
    [exchangeRate, isFiatDisplay, userCurrency]
  );

  return { formatHprToFiatValue, isFiatDisplay };
}
